/**
 * Client metadata — identifies which app binary is talking to the backend.
 *
 * The same self-hosted Fastify backend serves the web app plus the iOS and
 * Android Capacitor shells. Store binaries linger in users' pockets for weeks
 * after a release (App Store / Play review + slow user updates), so the backend
 * must know *who* is calling to (a) branch safely and (b) measure how many
 * users are still on old binaries before any contract is retired.
 *
 * Every request carries:
 *   X-Pawseum-Client         — "web" | "ios" | "android"
 *   X-Pawseum-Client-Version — versionName / semver (or build-time VITE_APP_VERSION)
 *   X-Pawseum-Client-Build   — native build number (versionCode / CFBundleVersion)
 *
 * On native the authoritative source is `@capacitor/app` App.getInfo(). It is
 * async, so we resolve it once at startup and cache it; headers read the cache
 * synchronously (falling back to the build-time version until it resolves).
 */
import { APP_TARGET, IS_NATIVE_APP } from "../config/appTarget.js";

export interface ClientMeta {
  platform: "web" | "ios" | "android";
  /** versionName / semver on native, or the build-time VITE_APP_VERSION on web. */
  version: string;
  /** Native build number (monotonic). `null` on web (no store build). */
  build: number | null;
}

const BUILD_TIME_VERSION = import.meta.env.VITE_APP_VERSION ?? "web";

let cached: ClientMeta = {
  platform: APP_TARGET as ClientMeta["platform"],
  version: BUILD_TIME_VERSION,
  build: null,
};

let initPromise: Promise<ClientMeta> | null = null;

/**
 * Resolve the native version/build once and cache it. Safe to call repeatedly —
 * the underlying work runs at most once. No-op (returns the web defaults) off
 * native.
 */
export function initClientMeta(): Promise<ClientMeta> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    if (IS_NATIVE_APP) {
      try {
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        const buildNum = Number.parseInt(info.build, 10);
        cached = {
          platform: APP_TARGET as ClientMeta["platform"],
          version: info.version || BUILD_TIME_VERSION,
          build: Number.isFinite(buildNum) ? buildNum : null,
        };
      } catch {
        // Keep the build-time defaults if the native plugin is unavailable.
      }
    }
    return cached;
  })();
  return initPromise;
}

/** Synchronous snapshot of the current client metadata (cached). */
export function getClientMeta(): ClientMeta {
  return cached;
}

/** Version headers to merge into every outbound request. */
export function getClientHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "X-Pawseum-Client": cached.platform,
    "X-Pawseum-Client-Version": cached.version,
  };
  if (cached.build != null) {
    headers["X-Pawseum-Client-Build"] = String(cached.build);
  }
  return headers;
}

// Fire-and-forget so the cache is populated as early as possible; callers that
// need the resolved value (e.g. the force-update gate) still await initClientMeta().
void initClientMeta();
